import { useEffect, useRef, useState } from 'react';

import { LIST_BUCKET, toursRpcArgs, toursRpcName } from '../list-queries';
import { getPrefetched } from '../static-prefetch';
import { supabase } from '../supabase';
import type { Tour } from '../tours';

export type ToursQuery = {
  city?: string;
  /** A `tours.themes` value (e.g. 'wine') — switches to the `tours_by_theme` RPC. */
  theme?: string;
  limit?: number;
};

/**
 * Mirrors use-events.ts: calls the `tours_by_city` RPC (or `tours_by_theme`
 * for a themed query). Null city → no filter.
 *
 * `seedKey` opts into the build-time seed so /[lang]/tours ships its cards as
 * real links in the static HTML instead of "Loading experiences…" — see
 * the app's src/lib/detail/list-prefetch.ts.
 */
export function useTours(query: ToursQuery = {}, seedKey?: string) {
  const [seed] = useState(() => getPrefetched<Tour[]>(LIST_BUCKET, seedKey));
  const [tours, setTours] = useState<Tour[]>(seed ?? []);
  const [loading, setLoading] = useState(seed === undefined);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(query);
  // See use-events.ts — suppresses the loading flash only for the seeded query.
  const seededKey = useRef(seed !== undefined ? key : null);

  useEffect(() => {
    let cancelled = false;
    const isSeededFirstRun = seededKey.current === key;
    seededKey.current = null;

    // A seeded list is already complete for what the page renders (see the
    // trimmers in the app's src/lib/detail/list-prefetch.ts), and it's no staler than the
    // static HTML around it — both come from the same daily build. Refetching
    // it on load would pull the same rows over the network a second time for
    // no visible change. Later runs (a city filter) fetch normally.
    if (isSeededFirstRun) return;

    setLoading(true);
    setError(null);

    supabase
      .rpc(toursRpcName(query), toursRpcArgs(query))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError(error.message);
          setTours([]);
        } else {
          setTours((data ?? []) as Tour[]);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { tours, loading, error };
}
